<template>
  <el-form
    ref="form"
    size="medium"
    v-loading="loading"
    :model="form"
    :rules="rules"
    :validate-on-rule-change="true"
    label-width="150px"
  >
    <div class="section-title">公司基础信息</div>
    <el-row>
      <el-col :span="24">
        <el-form-item :label="$t('person.companyName')" prop="companyName" key="companyName">
          <el-input v-model="form.companyName" :placeholder="$t('person.namePlaceholder')" :disabled="isEdit" class="form-field"></el-input>
        </el-form-item>
      </el-col>
      <el-col :span="24">
        <el-form-item :label="$t('person.license')" prop="companyLicenseList">
          <verify-uploader
            action="/action/upload"
            list-type="picture-card"
            :accept="`.${imageType.join(',.')}`"
            :multiple="true"
            :disabled="licenseUploading || isEdit"
            :file-list="form.companyLicenseList"
            :before-upload="handleLicenseBefore"
            :on-preview="handleLicensePreview"
            :on-success="handleLicenseSuccess"
            :on-remove="handleLicenseRemove"
            :on-error="handleLicenseError"
          >
            <i class="el-icon-plus" />
            <div slot="tip" class="el-upload__tip">
              <div>· 请上传统一社会信用代码清晰可见的相关资质照片</div>
              <div>· 复印件需手写“与原件一致”，并于文字处加盖公司鲜章</div>
              <div>· 支持jpg、png、jpeg格式图片，且大小不能大于10MB</div>
            </div>
          </verify-uploader>
        </el-form-item>
      </el-col>
      <el-col :span="24">
        <el-form-item :label="$t('person.legalPerson')" prop="corporation">
          <el-input v-model="form.corporation" :placeholder="$t('person.legalPersonPlaceholder')" :disabled="isEdit" class="form-field"></el-input>
        </el-form-item>
      </el-col>
      <el-col :span="24">
        <el-form-item :label="$t('person.logo')" prop="companyPhotoList">
          <verify-uploader
            action="/action/upload"
            list-type="picture-card"
            :accept="`.${imageType.join(',.')}`"
            :class="{'has-logo': form.companyPhotoList.length}"
            :file-list="form.companyPhotoList"
            :disabled="logoUploading"
            :before-upload="handleLogoBefore"
            :on-preview="handleLogoPreview"
            :on-success="handleLogoSuccess"
            :on-remove="handleLogoRemove"
            :on-error="handleLogoError"
          >
            <i v-if="form.companyPhotoList.length === 0" class="el-icon-plus" />
            <div slot="tip" class="el-upload__tip">
              <div>1. {{ $t('person.logoRule') }}</div>
            </div>
          </verify-uploader>
        </el-form-item>
      </el-col>
      <el-col :span="24">
        <el-form-item :label="$t('person.desc')" prop="companyDesc">
          <el-input v-model="form.companyDesc" type="textarea" class="form-field" placeholder="请输入公司相关介绍内容，不多于500字"></el-input>
        </el-form-item>
      </el-col>

      <el-col v-if="!isSigned" :span="24">
        <el-form-item
          :label="$t('person.production')"
          :rules="[{type: 'array', required: true, message: '请上传作品信息', trigger: 'change'}]"
          prop="productionList"
        >
          <verify-uploader
            action="/action/upload?type=excel"
            accept=".xlsx"
            :limit="1"
            :disabled="isEdit"
            :file-list="form.productionList"
            :on-success="handleProductionSuccess"
            :on-remove="handleProductionRemove"
            :on-error="handleProductionError"
          >
            <el-button type="primary">上 传</el-button>
            <div slot="tip" class="el-upload__tip">
              <a href="/file/registry_template.xlsx" download="非签约用户线上申请权限歌曲提交信息.xlsx">下载模板</a>
              <span class="danger-word m-l-md">非签约用户提交歌曲信息进行签约评估</span>
            </div>
          </verify-uploader>
        </el-form-item>
      </el-col>

      <div class="section-title">联系人信息</div>
      <el-col :span="24">
        <el-form-item :label="$t('person.email')" prop="email">
          <el-input
            v-model="form.email"
            :placeholder="$t('person.emailPlaceholder')"
            class="form-field"
            type="email"
            autocomplete
          ></el-input>
        </el-form-item>
      </el-col>
      <el-col :span="24">
        <el-form-item :label="$t('person.address')" prop="cpAddress">
          <el-input v-model="form.cpAddress" :placeholder="$t('person.addressPlaceholder')" class="form-field"></el-input>
        </el-form-item>
      </el-col>
      <el-col :span="24">
        <el-form-item :label="$t('person.phone')" prop="phoneNum">
          <el-input v-model="form.phoneNum" :placeholder="$t('person.phonePlaceholder')" class="form-field"></el-input>
        </el-form-item>
      </el-col>
      <el-col :span="24">
        <el-form-item :label="$t('person.qq')" prop="qqNum">
          <el-input v-model="form.qqNum" :placeholder="$t('person.qqPlaceholder')" class="form-field"></el-input>
        </el-form-item>
      </el-col>

      <!-- 签约厂牌填写 -->
      <section v-if="isSigned" class="other-info">
        <div class="section-title">其他信息</div>

        <el-col :span="24">
          <el-form-item prop="isRelate">
            <template #label>
              <span style="line-height: 24px;">是否存在关联厂牌<br>(含代理关系)</span>
            </template>
            <el-radio-group v-model="form.isRelate" :disabled="isEdit">
              <el-radio v-for="(name, id) in isRelateMap" :key="+id" :label="+id">{{ name }}</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-col>

        <el-col v-if="form.isRelate" :span="24">
          <el-form-item label="当前厂牌身份" prop="companyRole">
            <el-radio-group v-model="form.companyRole" :disabled="isEdit">
              <el-radio v-for="(name, id) in companyRoleMap" :key="+id" :label="+id">{{ name }}</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-col>

        <el-col v-if="form.isRelate && form.companyRole !== 1" :span="24">
          <el-form-item prop="copyrightCompany.cpCompanyName">
            <template #label>
              <span>{{ cpCompanyShowText }}</span>
            </template>
            <el-input
              v-model="form.copyrightCompany.cpCompanyName"
              :disabled="!!form.copyrightCompany.cpCompanyId"
              :placeholder="$t('person.cpCompanyPlaceholder')"
              class="form-field"
            ></el-input>
          </el-form-item>
        </el-col>

        <el-col v-if="form.isRelate && form.companyRole === 1" :span="24">
          <el-form-item>
            <template #label>
              <span style="line-height: 24px;">下属子厂牌列表<br>(含代理关系)</span>
            </template>
            <div>
              <i v-if="!isEdit" class="el-icon-plus" @click="handleAddAgentCompany" />
            </div>
            <ul>
              <li v-for="(agc, index) in form.agentCompanyList" :key="agc.tempId">
                <el-input
                  v-model.trim="agc.agentCompanyName"
                  :readonly="!!agc.agentCompanyId"
                  :aria-disabled="!!agc.agentCompanyId"
                  :class="{approved: !!agc.agentCompanyId, 'cp-input': true}"
                  placeholder="输入公司名"
                >
                </el-input>
                <span v-if="!!agc.agentCompanyId" class="status">{{ $t('person.checked') }}</span>
                <i v-if="!isEdit" class="el-icon-delete" @click="handleRemoveAgent(agc, index)" />
              </li>
            </ul>
          </el-form-item>
        </el-col>
      </section>
    </el-row>
  </el-form>
</template>

<script>
import { mapGetters } from 'vuex'
import { imageType } from '@/utils/constants'
import { emailValidate, validateFileExt } from '@/utils/validate'
import VerifyUploader from '@/components/VerifyUploader'
import { companyRoleMap, isRelateMap, Company } from '../const'

export default {
  name: 'CompanyEditForm',
  components: {
    VerifyUploader
  },
  props: {
    defaultData: Object,
    loading: Boolean,
    isSigned: Boolean,
  },
  data() {
    const isZH = this.$i18n.locale === 'zh'

    const emailValidator = (rule, value, cb) => {
      if (emailValidate(value)) {
        cb()
      } else {
        cb(new Error('请输入正确的邮箱'))
      }
    }

    return {
      imageType,
      isRelateMap,
      companyRoleMap,

      form: new Company({ signStatus: this.isSigned ? 1 : 0 }),
      rules: {
        companyName: [
          { required: true, message: isZH ? '请填写公司名' : 'Please input your label name', trigger: 'blur' }
        ],
        corporation: [
          { required: true, message: isZH ? '请填写法人代表' : 'Please input the legal person', trigger: 'blur' }
        ],
        email: [
          { required: true, message: isZH ? '请填写邮箱' : 'Please input your email', trigger: 'blur' },
          { validator: emailValidator, trigger: 'blur' }
        ],
        cpAddress: [
          { required: true, message: isZH ? '请填写联系地址' : 'Please input your address', trigger: 'blur' }
        ],
        phoneNum: [
          { required: true, message: isZH ? '请填写联系电话' : 'Please input your phone number', trigger: 'blur' },
        ],
        companyLicenseList: [
          { type: 'array', required: true, message: isZH ? '请上传营业执照' : 'Please upload your Business Certificate', trigger: 'change' }
        ],
        companyPhotoList: [
          { type: 'array', required: true, message: isZH ? '请上传公司LOGO' : 'Please upload your icon & logos', trigger: 'change' }
        ],
        'copyrightCompany.cpCompanyName': [
          { required: true, message: '请填写版权所属厂牌', trigger: 'blur' },
        ]
      },
      licenseUploadNum: 0,
      licenseUploading: false,
      logoUploading: false,
    }
  },
  computed: {
    ...mapGetters(['company', 'relateCompanyList', 'agentCompanyList']),
    cpCompanyShowText() {
      return [1, 3].includes(this.form.companyRole) ? '版权所属厂牌' : '母厂牌名称'
    },
    isEdit() {
      return !!this.defaultData
    }
  },
  created() {
    this.init()
  },
  mounted() {
    this.$refs.form.clearValidate()
  },
  methods: {
    init() {
      if (this.defaultData) {
        this.form = new Company(this.defaultData)
      }
    },

    handleLicenseBefore(file) {
      if (!validateFileExt(imageType, file.name)) {
        this.$message.warning('不支持的文件类型')
        return false
      }

      this.licenseUploading = true
      this.licenseUploadNum++
    },
    handleLicenseSuccess(resp, file, fileList) {
      const respData = resp.data
      const fileData = {
        url: this.$toPreview(respData.url),
        md5: respData.md5,
        fileName: file.name,
        photoPath: respData.file_id,
        photoUrl: respData.url,
        photoSize: file.size,
        photoHeight: respData.file_info.height,
        photoWidth: respData.file_info.width,
        orderIndex: 0,
      }
      this.form.companyLicenseList.push(fileData)

      if (this.licenseUploadNum === this.form.companyLicenseList.length) {
        this.licenseUploading = false
      }
    },
    handleLicenseError(err, file, fileList) {
      this.form.companyLicenseList = fileList
      this.licenseUploadNum--
      if (this.licenseUploadNum === this.form.companyLicenseList.length) {
        this.licenseUploading = false
      }
      this.$message({
        type: 'error',
        message: '营业执照上传失败: ' + err.message,
        error: err
      })
    },
    handleLicenseRemove(file, fileList) {
      this.form.companyLicenseList = fileList
      this.licenseUploadNum--
    },
    handleLicensePreview(file) {
      open(file.url, '_blank')
    },

    handleLogoBefore(file) {
      if (!validateFileExt(imageType, file.name)) {
        this.$message.warning('不支持的文件类型')
        return Promise.reject()
      }
      if (this.form.companyPhotoList.length === 0) {
        this.logoUploading = true
        return true
      }

      return this.$confirm('只能有一张 Logo 图，是否要覆盖?', '提示', {
        type: 'warning'
      }).then(_ => {
        this.form.companyPhotoList.pop()
        this.logoUploading = true
        return Promise.resolve()
      }).catch(_ => {
        return Promise.reject()
      })
    },
    handleLogoSuccess(resp, file, fileList) {
      const respData = resp.data
      const fileData = {
        url: this.$toPreview(respData.url),
        md5: respData.md5,
        photoPath: respData.file_id,
        photoUrl: respData.url,
        photoSize: file.size,
        photoHeight: respData.file_info.height,
        photoWidth: respData.file_info.width,
        orderIndex: 0,
      }
      this.form.companyPhotoList = [fileData]
      this.logoUploading = false
    },
    handleLogoError(err, file, fileLis) {
      this.form.companyPhotoList.pop()
      this.logoUploading = false
      this.$message({
        type: 'error',
        message: 'Logo上传失败: ' + err.message,
        error: err
      })
    },
    handleLogoRemove(file, fileList) {
      this.form.companyPhotoList = fileList
    },
    handleLogoPreview(file) {
      open(file.url, '_blank')
    },

    handleProductionSuccess(resp, file, fileList) {
      const respData = resp.data
      const fileData = {
        name: file.name,
        url: this.$toPreview(respData.url),
        md5: respData.md5,
        fileName: file.name,
        filePath: respData.file_id,
        fileUrl: respData.url,
        fileSize: file.size,
      }
      this.form.productionList.push(fileData)
    },
    handleProductionError(err, file, fileList) {
      this.form.productionList = []
      this.$message({
        type: 'error',
        message: '作品信息上传失败: ' + err.message,
        error: err
      })
    },
    handleProductionRemove(file, fileList) {
      this.form.productionList = fileList
    },

    handleAddAgentCompany() {
      this.form.agentCompanyList.push({
        tempId: `${Math.random()}`,
        agentCompanyName: '',
        cpCompanyId: this.form.company
      })
    },
    handleRemoveAgent(agentCp, index) {
      this.$alert(`确认删除代理厂牌${agentCp.agentCompanyName ? `（${agentCp.agentCompanyName}）` : ''}`, '提示', {
        type: 'warning'
      }).then(_ => {
        this.form.agentCompanyList.splice(index, 1)
      }).catch(_ => {})
    },

    handleAddRelateCompany() {
      this.form.relateCompanyList.push({
        tempId: `${Math.random()}`,
        relateCompanyName: '',
        relateCompanyId: ''
      })
    },
    handleRemoveRelate(relateCP, index) {
      this.$alert(`确认删除关联厂牌（${relateCP.relateCompanyName}）`, '提示', {
        type: 'warning'
      }).then(_ => {
        this.form.relateCompanyList.splice(index, 1)
      }).catch(_ => {})
    },

    async genData() {
      const valid = await this.$refs.form.validate().catch(_ => {
        this.$alert('有字段未填写完整, 请检查', '提示', { type: 'warning' })
      })

      if (!valid) throw new Error('表单校验失败')

      const postData = this.form.getBackendData()
      return postData
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables.scss";

.other-info {
  /deep/ .el-form-item {
    margin-bottom: 16px
  }
}

.form-field {
  width: 600px;
}
.el-icon-plus {
  cursor: pointer;
  color: $success;
}
.el-icon-delete {
  cursor: pointer;
  color: $danger;
  margin-left: 8px;
}
.has-logo {
  /deep/ .el-upload--picture-card {
    display: none;
  }
}
.el-upload__tip {
  div {
    line-height: 2;
  }
}

.cp-input {
  margin-bottom: 4px;
  width: 400px;

  &.approved {
    +.status {
      position: absolute;
      left: 335px;
      color: rgb(85, 180, 85);
      font-style: italic;
    }
  }
}
.el-icon-plus {
  font-weight: 700;
}
</style>
